import cn from 'clsx'
import { useDispatch, useSelector } from 'react-redux'
import { throttle } from 'lodash'

// Actions
import { actions as audioActions } from 'modules/audio/actions'
import { actions as collectionsActions } from 'modules/collections/actions'

// Components
import { Metronome, IconPlay, IconPause, IconFilledPlayFat, IconFilledPauseFat, IconPlaySmall, IconPauseSmall } from '@tracklib/kit'

// Hooks
import { useRequest } from 'modules/requests/hooks'

// Types
import { State, Loop, AudioState, DiscoverEntityType } from 'types'

import styles from './playbutton.css'

type Value = { songItemId: string; trackId: number; loop?: Loop }
type PlayButtonProps = {
  type?: 'bordered'
  className?: string
  isFake?: boolean
  entity: DiscoverEntityType
  value: string | Value
  contextKey?: string | null
  datatesting?: string
  compact?: boolean
  hideLoading?: boolean
  onPlay?: () => void
  hoverOnly?: boolean
  animated?: boolean
}

const PlayButton = ({
  className,
  compact,
  contextKey,
  datatesting,
  entity,
  hideLoading,
  hoverOnly,
  isFake,
  onPlay,
  type,
  value,
  animated,
}: PlayButtonProps): JSX.Element => {
  const dispatch = useDispatch()
  const songItemId = (value as Value)?.songItemId || value
  const audio = useSelector<State, AudioState>(state => state.audio)
  const playlistString = entity === 'genre' ? `{"genres":"${value}"}` : `{"${entity}":"${value}"}`

  const isActive =
    entity === 'track' ? songItemId === audio.songItemId && audio.currentPlaylist === contextKey : audio.currentPlaylist === playlistString

  const searchQueryLoading = useRequest('PREPARE_PLAY_SEARCH_QUERY').isLoading
  const collectionLoading = useRequest('PREPARE_PLAY_COLLECTION').isLoading
  const isLoading = (isActive && audio.isLoading) || (isActive && searchQueryLoading) || (isActive && collectionLoading)

  const isPlaying = isActive && audio.isPlaying && !audio.isPaused
  const isLooping = isActive && audio.isLooping
  const isLoadingLoop = useRequest('FETCH_TRACK_FEATURE_DATA').isLoading

  const renderContent = () => {
    if (isLoading && !hideLoading) {
      return <Metronome className={cn(styles.metronome, ((value as Value)?.loop || isLoadingLoop) && styles.metronomeLoopLoading)} />
    }
    if (type === 'bordered') {
      return !isPlaying ? <IconPlaySmall /> : <IconPauseSmall />
    }
    if (type === 'bordered') {
      return !isPlaying ? <IconPlay /> : <IconPause />
    }
    return !isPlaying ? <IconFilledPlayFat /> : <IconFilledPauseFat />
  }
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!isLoading || !isLoadingLoop) {
      if (isActive) {
        dispatch(audioActions.togglePaused())
      } else {
        if (onPlay) {
          onPlay()
        }
        switch (entity) {
          case 'label':
            return dispatch(audioActions.playSearchQuery({ label: String(value) }))
          case 'collection':
            return dispatch(collectionsActions.playCollection(+value))
          case 'artist':
            return dispatch(audioActions.playSearchQuery({ artist: String(value) }))
          case 'genre':
            return dispatch(audioActions.playSearchQuery({ genres: value }))
          case 'track': {
            // songItemId and loop only needed if loop
            if ((value as Value).songItemId) {
              return dispatch(
                audioActions.playTrack(
                  (value as Value)?.songItemId,
                  (value as Value)?.trackId,
                  contextKey as string,
                  true,
                  (value as Value)?.loop,
                ),
              )
            }
            // use value/trackId as songItemId instead if not explicitly set
            return dispatch(audioActions.playTrack(String(value), +value, contextKey as string, true, null))
          }
          default:
            return null
        }
      }
    }

    return null
  }

  const throttledHandleClick = throttle(handleClick, 500)
  const classNames = cn(
    styles.play,
    type === 'bordered' && styles.bordered,
    compact && styles.compact,
    isLooping && styles.isLooping,
    className,
    isActive && styles.isActive,
    hoverOnly && styles.hoverOnly,
    animated && !isActive && !isLoading && !isPlaying && !isLoadingLoop && styles.pulse,
  )
  return isFake ? (
    <div className={classNames} datatesting={datatesting} data-play="true">
      {renderContent()}
    </div>
  ) : (
    <button className={classNames} datatesting={datatesting} data-play="true" onClick={throttledHandleClick}>
      {renderContent()}
    </button>
  )
}

export default PlayButton
